import { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "../../lib/api";
import BulkUploadWizard from "./BulkUploadWizard";
import MasterInvitesSection from "./MasterInvitesSection";
import { PUBLIC_ORIGIN, inviteUrl, timeAgo, copyToClipboard } from "../../lib/inviteUi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Plus, Copy, Check, ChevronRight, Link2, MessageCircle, Mail, Code, ExternalLink, Upload } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin — customer-invite management. Sibling of the "Referrals" tab.
 *
 * Two views (sub-tabs):
 *   - Individual invites: one link per known customer (this file).
 *   - Master links: one reusable blast link tracked per URL variable
 *     (see MasterInvitesSection.jsx).
 *
 * Endpoints used (all defined in /app/backend/server.py):
 *   POST /api/admin/invites  · GET /api/admin/invites
 *   GET  /api/invites/resolve/{slug}  (public — used for copy-preview only)
 */

function whatsappHref(url, name) {
  const msg = name
    ? `Hi ${name}, here's your personal loan application link — ${url}`
    : `Here's your personal loan application link — ${url}`;
  return `https://wa.me/?text=${encodeURIComponent(msg)}`;
}

function emailHref(url, name) {
  const subject = "Your loan application link";
  const body = name
    ? `Hi ${name},\n\nHere's your personal loan application link:\n${url}\n\n— Offer Express`
    : `Here's your personal loan application link:\n${url}\n\n— Offer Express`;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function htmlSnippet(url, name) {
  const label = name ? `Apply now — ${name}` : "Apply now";
  return `<a href="${url}" style="display:inline-block;padding:12px 20px;background:#0055FF;color:#fff;text-decoration:none;border-radius:8px;font-family:Inter,sans-serif;font-weight:600;font-size:14px;">${label}</a>`;
}

export default function InvitesSection() {
  const [invites, setInvites] = useState([]);
  const [summary, setSummary] = useState({ total: 0, opened: 0, phone_entered: 0, redirected: 0 });
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [detail, setDetail] = useState(null); // invite row currently shown in the drawer
  const [successInvite, setSuccessInvite] = useState(null);
  const [filter, setFilter] = useState("all");
  // "single" = one link per known customer · "master" = reusable blast link
  // tracked per URL variable (see MasterInvitesSection).
  const [view, setView] = useState("single");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: inv }, { data: prods }] = await Promise.all([
        api.get("/admin/invites"),
        api.get("/products?channel=all"),
      ]);
      setInvites(inv?.invites || []);
      setSummary(inv?.summary || { total: 0, opened: 0, phone_entered: 0, redirected: 0 });
      // Show every product an admin can send someone to — both banking (loans,
      // cards) and ecom (cashback links). Inactive products are hidden.
      setProducts((prods || []).filter((p) => p.active !== false));
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed to load invites");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Master links live in their own sub-tab; funnel chips only apply to
  // single (per-customer) invites.
  const singleInvites = useMemo(
    () => invites.filter((i) => i.invite_type !== "master"),
    [invites]
  );
  const masterInvites = useMemo(
    () => invites.filter((i) => i.invite_type === "master"),
    [invites]
  );
  // Apply the active funnel chip. Filters are mutually exclusive.
  const filteredInvites = useMemo(() => matchFilter(singleInvites, filter), [singleInvites, filter]);

  return (
    <div data-testid="admin-invites-section">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-2xl tracking-tight">Invites</h2>
          {view === "single" && (
            <div className="mt-1 text-[13px] text-neutral-600 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span><b className="text-black">{summary.total}</b> total</span>
              <span className="text-neutral-300">·</span>
              <span><b className="text-black">{summary.opened}</b> opened</span>
              <span className="text-neutral-300">·</span>
              <span><b className="text-black">{summary.phone_entered}</b> phone entered</span>
              <span className="text-neutral-300">·</span>
              <span><b className="text-black">{summary.redirected}</b> redirected</span>
            </div>
          )}
        </div>
        {view === "single" && (
          <div className="flex gap-2">
            <Button
              data-testid="bulk-invite-btn"
              variant="outline"
              onClick={() => setBulkOpen(true)}
            >
              <Upload className="w-4 h-4 mr-1.5" /> Bulk upload
            </Button>
            <Button
              data-testid="new-invite-btn"
              onClick={() => setCreateOpen(true)}
              className="bg-black text-white hover:bg-[#0055FF]"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New invite
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-4" data-testid="invite-view-tabs">
        {[["single", "Individual invites"], ["master", "Master links"]].map(([key, label]) => (
          <button
            key={key}
            data-testid={`invite-tab-${key}`}
            onClick={() => setView(key)}
            className={
              "px-4 py-2 rounded-lg text-sm font-semibold border transition-colors " +
              (view === key
                ? "bg-black text-white border-black"
                : "bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50")
            }
          >
            {label}
            {key === "master" && masterInvites.length > 0 && (
              <span className={
                "ml-2 inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full text-[10px] font-semibold px-1 " +
                (view === key ? "bg-white/20 text-white" : "bg-neutral-100 text-neutral-600")
              }>{masterInvites.length}</span>
            )}
          </button>
        ))}
      </div>

      {view === "master" ? (
        <MasterInvitesSection invites={masterInvites} products={products} onChanged={load} />
      ) : (
        <>
          <FunnelChips invites={singleInvites} filter={filter} onChange={setFilter} />

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Opened</TableHead>
              <TableHead>Phone: typed → submitted</TableHead>
              <TableHead>Redirected to</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-neutral-500">Loading…</TableCell></TableRow>
            ) : singleInvites.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-neutral-500">
                No invites yet. Click <b>New invite</b> to create the first one.
              </TableCell></TableRow>
            ) : filteredInvites.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-neutral-500">
                No invites match this filter.{" "}
                <button className="underline underline-offset-2" onClick={() => setFilter("all")}>Clear filter</button>
              </TableCell></TableRow>
            ) : filteredInvites.map((inv) => (
              <InviteRow key={inv.slug} inv={inv} onOpen={() => setDetail(inv)} />
            ))}
          </TableBody>
        </Table>
      </div>
        </>
      )}

      <CreateInviteDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        products={products}
        onCreated={(created) => {
          setInvites((prev) => [created, ...prev]);
          setSummary((s) => ({ ...s, total: s.total + 1 }));
          setSuccessInvite(created);
          setCreateOpen(false);
        }}
      />

      <BulkUploadWizard
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        products={products}
        publicOrigin={PUBLIC_ORIGIN}
        onDone={() => load()}
      />

      <SuccessDialog
        invite={successInvite}
        onClose={() => setSuccessInvite(null)}
      />

      <DetailDialog
        invite={detail}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}

// ============================================================================
// Row (memoised — cheap enough to skip React.memo)
// ============================================================================
function InviteRow({ inv, onOpen }) {
  const url = inviteUrl(inv.slug);
  const typed = inv.typed_phone || "";
  const captured = inv.captured_phone || "";
  const matches = inv.phone_matched === true;
  const notMatches = inv.phone_matched === false;
  return (
    <TableRow
      data-testid={`invite-row-${inv.slug}`}
      className="cursor-pointer hover:bg-neutral-50"
      onClick={onOpen}
    >
      <TableCell>
        <div className="font-medium">{timeAgo(inv.created_at)}</div>
        <div className="text-[11px] text-neutral-500">{inv.created_at?.slice(0, 10)}</div>
      </TableCell>
      <TableCell>
        <div className="font-medium tabular-nums">+91 {(inv.customer_phone || "").replace(/(\d{5})(\d{5})/, "$1 $2")}</div>
        <div className="text-[11px] text-neutral-500">{inv.customer_name || <span className="text-neutral-400">— no name</span>}</div>
      </TableCell>
      <TableCell>
        {inv.product_title ? (
          <>
            <div className="font-medium">{inv.product_provider || inv.product_title}</div>
            {inv.skip_capture && <Badge variant="outline" className="mt-1 text-[10px]">Skip capture</Badge>}
          </>
        ) : (
          <>
            <div className="font-medium">Any</div>
            {inv.chosen_product_title
              ? <Badge className="mt-1 text-[10px]" style={{ background: "#f0f4ff", color: "#0055FF" }}>chose {inv.chosen_product_title}</Badge>
              : <div className="text-[11px] text-neutral-400 mt-0.5">Not chosen yet</div>}
          </>
        )}
      </TableCell>
      <TableCell>
        {inv.opened_at ? (
          <>
            <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-emerald-200">✓ {timeAgo(inv.opened_at)}</Badge>
            <div className="text-[11px] text-neutral-500 mt-0.5">{inv.open_count} open{inv.open_count === 1 ? "" : "s"}</div>
          </>
        ) : <Badge variant="outline" className="text-neutral-500">Not opened</Badge>}
      </TableCell>
      <TableCell>
        {(typed || captured) ? (
          <>
            <div className="font-mono text-[12px] flex items-center gap-1.5">
              <span>{typed || <span className="text-neutral-300">—</span>}</span>
              <span className="text-neutral-300">→</span>
              <span>{captured || <span className="text-neutral-300">—</span>}</span>
              {matches && <span className="w-2 h-2 rounded-full bg-emerald-500 ml-1" title="matches" />}
              {notMatches && <span className="w-2 h-2 rounded-full bg-red-500 ml-1" title="mismatch" />}
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              {captured
                ? (matches ? "Match ✓" : "Mismatch — different number submitted")
                : <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-[10px]">Abandoned before submit</Badge>}
            </div>
          </>
        ) : <span className="text-neutral-400 text-sm">—</span>}
      </TableCell>
      <TableCell>
        {inv.final_redirect_url ? (
          <div className="max-w-[240px]">
            <a
              href={inv.final_redirect_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="font-mono text-[11px] text-sky-700 hover:underline block truncate bg-sky-50 px-2 py-1 rounded border border-sky-100"
              title={inv.final_redirect_url}
            >
              {inv.final_redirect_url.replace(/^https?:\/\//, "")}
            </a>
          </div>
        ) : <span className="text-neutral-400 text-sm">—</span>}
      </TableCell>
      <TableCell>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            title="Copy invite link"
            className="w-8 h-8 grid place-items-center rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-100 hover:text-black"
            onClick={() => copyToClipboard(url)}
            data-testid={`invite-copy-${inv.slug}`}
          ><Copy className="w-3.5 h-3.5" /></button>
          <button
            title="View detail"
            className="w-8 h-8 grid place-items-center rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-100 hover:text-black"
            onClick={onOpen}
            data-testid={`invite-detail-${inv.slug}`}
          ><ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// Create dialog
// ============================================================================
function CreateInviteDialog({ open, onOpenChange, products, onCreated }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [productId, setProductId] = useState("");
  const [skipCapture, setSkipCapture] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset after close so the next open starts fresh.
      setPhone(""); setName(""); setProductId(""); setSkipCapture(false); setBusy(false);
    }
  }, [open]);

  // `skip_capture` only makes sense for product-specific invites.
  const canSkip = !!productId;

  const submit = async (e) => {
    e.preventDefault();
    // Client-side normalise same rules as MobileCaptureDialog.
    let d = phone.replace(/\D/g, "");
    if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
    else if (d.length === 13 && d.startsWith("091")) d = d.slice(3);
    else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
    if (d.length !== 10 || !/^[6-9]/.test(d)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", {
        customer_phone: d,
        customer_name: name || undefined,
        product_id: productId || undefined,
        skip_capture: canSkip ? skipCapture : false,
      });
      onCreated?.(data);
      toast.success("Invite created");
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Could not create invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Create customer invite</DialogTitle>
          <p className="text-sm text-neutral-600">Personal link, tracked from open → phone → redirect</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Mobile number *</Label>
            <Input
              data-testid="invite-phone"
              inputMode="tel"
              maxLength={14}
              placeholder="+91 98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Customer name (optional)</Label>
            <Input
              data-testid="invite-name"
              placeholder="e.g. Sunil Verma"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Product</Label>
            <Select value={productId || "any"} onValueChange={(v) => setProductId(v === "any" ? "" : v)}>
              <SelectTrigger data-testid="invite-product"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any product — customer picks on landing</SelectItem>
                {/* Grouped so admin can scan banking vs. e-commerce at a glance. */}
                {(() => {
                  const banking = products.filter((p) => p.type === "banking");
                  const ecom    = products.filter((p) => p.type === "ecom");
                  const other   = products.filter((p) => p.type !== "banking" && p.type !== "ecom");
                  const group = (label, arr) => arr.length ? (
                    <div key={label}>
                      <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-semibold">{label}</div>
                      {arr.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                      ))}
                    </div>
                  ) : null;
                  return [
                    group("Banking / Loans", banking),
                    group("E-commerce / Cashback", ecom),
                    group("Other", other),
                  ];
                })()}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-neutral-500 mt-1">
              Leave as “Any product” for a generic invite. Customer will see the lender grid and we'll record which one they choose.
            </p>
          </div>
          {canSkip && (
            <label className="flex items-start gap-2.5 rounded-lg border border-neutral-200 p-3 cursor-pointer hover:bg-neutral-50">
              <input
                type="checkbox"
                data-testid="invite-skip-capture"
                checked={skipCapture}
                onChange={(e) => setSkipCapture(e.target.checked)}
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-medium">Skip mobile capture — redirect straight to partner</div>
                <p className="text-[11px] text-neutral-500 mt-1">
                  Use when the mobile number is already verified. We'll auto-identify the customer with this number and send them straight to the lender.
                </p>
              </div>
            </label>
          )}
          <Button
            type="submit"
            data-testid="invite-submit"
            disabled={busy}
            className="w-full h-11 bg-[#0055FF] text-white hover:opacity-90"
          >
            {busy ? "Creating…" : (<>Generate invite link <ChevronRight className="w-4 h-4 ml-1" /></>)}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Success dialog — copyable link + share options + HTML snippet
// ============================================================================
function SuccessDialog({ invite, onClose }) {
  if (!invite) return null;
  const url = inviteUrl(invite.slug);
  const html = htmlSnippet(url, invite.customer_name);

  return (
    <Dialog open={!!invite} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="invite-success-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 grid place-items-center mb-2">
            <Check className="w-6 h-6 text-emerald-600" />
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">Invite created</DialogTitle>
          <p className="text-sm text-neutral-600">
            For {invite.customer_name || "customer"} · +91 {(invite.customer_phone || "").replace(/(\d{5})(\d{5})/, "$1 $2")}
            {invite.product_title ? ` · ${invite.product_title}` : " · any product"}
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Invite link</Label>
            <div className="flex items-stretch border border-neutral-200 rounded-lg overflow-hidden">
              <code className="flex-1 px-3 py-2.5 font-mono text-[13px] bg-neutral-50 truncate" title={url}>{url}</code>
              <button
                className="border-l border-neutral-200 bg-white hover:bg-neutral-50 px-3 flex items-center gap-1 text-sm font-medium"
                onClick={() => copyToClipboard(url)}
              ><Copy className="w-3.5 h-3.5" /> Copy</button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Share via</Label>
            <div className="grid grid-cols-3 gap-2">
              <a
                href={whatsappHref(url, invite.customer_name)}
                target="_blank" rel="noreferrer"
                className="flex flex-col items-center gap-1.5 py-3 px-2 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-xs font-semibold"
              ><MessageCircle className="w-5 h-5 text-emerald-600" /> WhatsApp</a>
              <a
                href={emailHref(url, invite.customer_name)}
                className="flex flex-col items-center gap-1.5 py-3 px-2 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-xs font-semibold"
              ><Mail className="w-5 h-5 text-[#0055FF]" /> Email</a>
              <button
                onClick={() => copyToClipboard(html)}
                className="flex flex-col items-center gap-1.5 py-3 px-2 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-xs font-semibold"
              ><Code className="w-5 h-5 text-neutral-800" /> HTML snippet</button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">HTML snippet — clickable in Gmail, Outlook, etc.</Label>
            <pre className="bg-neutral-900 text-neutral-100 rounded-lg p-3 font-mono text-[11px] overflow-x-auto whitespace-pre-wrap break-all">{html}</pre>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Row detail drawer
// ============================================================================
function DetailDialog({ invite, onClose }) {
  if (!invite) return null;
  const url = inviteUrl(invite.slug);
  return (
    <Dialog open={!!invite} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="invite-detail-dialog">
        <DialogHeader>
          <Badge variant="outline" className="font-mono text-[11px] w-fit mb-2">{invite.slug}</Badge>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {invite.customer_name || "Customer"}
          </DialogTitle>
          <p className="text-sm text-neutral-600">
            +91 {(invite.customer_phone || "").replace(/(\d{5})(\d{5})/, "$1 $2")}
            {invite.product_title ? ` · ${invite.product_title}` : " · any product"}
          </p>
        </DialogHeader>
        <dl className="grid grid-cols-[130px_1fr] gap-x-6 gap-y-3 text-sm mt-2">
          <dt className="text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold pt-0.5">Created</dt>
          <dd>{new Date(invite.created_at).toLocaleString()} · by {invite.created_by_email || "admin"}</dd>

          <dt className="text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold pt-0.5">Product target</dt>
          <dd>
            {invite.product_title || <span className="text-neutral-500">Any (customer picks)</span>}
            {invite.skip_capture && <Badge variant="outline" className="ml-2 text-[10px]">Skip capture</Badge>}
          </dd>

          {invite.chosen_product_title && (<>
            <dt className="text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold pt-0.5">Product chosen</dt>
            <dd>{invite.chosen_product_title} <span className="text-neutral-500">· {timeAgo(invite.chosen_at)}</span></dd>
          </>)}

          <dt className="text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold pt-0.5">Opened</dt>
          <dd>
            {invite.opened_at ? (
              <>
                <b>{new Date(invite.opened_at).toLocaleString()}</b>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  {invite.open_count} open{invite.open_count === 1 ? "" : "s"}
                  {invite.first_ua ? ` · ${invite.first_ua.slice(0, 60)}` : ""}
                  {invite.first_ip ? ` · IP ${invite.first_ip}` : ""}
                </div>
              </>
            ) : <span className="text-neutral-500">Not opened yet</span>}
          </dd>

          <dt className="text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold pt-0.5">Phone typed</dt>
          <dd>
            {invite.typed_phone ? (
              <>
                <span className="font-mono text-[12px] bg-neutral-100 px-1.5 py-0.5 rounded">{invite.typed_phone}</span>
                <div className="text-[11px] text-neutral-500 mt-0.5">Last keystroke {timeAgo(invite.typed_at)}</div>
              </>
            ) : <span className="text-neutral-500">Nothing typed yet</span>}
          </dd>

          <dt className="text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold pt-0.5">Phone submitted</dt>
          <dd>
            {invite.captured_phone ? (
              <>
                <span className="font-mono text-[12px] bg-neutral-100 px-1.5 py-0.5 rounded">{invite.captured_phone}</span>
                {invite.phone_matched
                  ? <Badge className="ml-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-emerald-200">✓ matches original</Badge>
                  : <Badge className="ml-2 bg-red-50 text-red-700 hover:bg-red-50 border-red-200">Mismatch</Badge>}
                <div className="text-[11px] text-neutral-500 mt-0.5">Submitted {timeAgo(invite.phone_captured_at)}</div>
              </>
            ) : <span className="text-neutral-500">Not submitted</span>}
          </dd>

          <dt className="text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold pt-0.5">Redirected to</dt>
          <dd>
            {invite.final_redirect_url ? (
              <>
                <a
                  href={invite.final_redirect_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-[11px] text-sky-700 hover:underline break-all"
                >{invite.final_redirect_url}</a>
                <div className="mt-1.5">
                  <a
                    href={invite.final_redirect_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#0055FF] hover:underline"
                  ><ExternalLink className="w-3 h-3" /> Open URL</a>
                </div>
              </>
            ) : <span className="text-neutral-500">Not redirected yet</span>}
          </dd>

          {invite.final_click_id && (<>
            <dt className="text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold pt-0.5">Click ID</dt>
            <dd><span className="font-mono text-[12px] bg-neutral-100 px-1.5 py-0.5 rounded">{invite.final_click_id}</span></dd>
          </>)}

          <dt className="text-[11px] uppercase tracking-[0.12em] text-neutral-500 font-semibold pt-0.5">Invite link</dt>
          <dd>
            <a href={url} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-sky-700 hover:underline break-all">{url}</a>
            <button onClick={() => copyToClipboard(url)} className="ml-2 text-xs font-medium underline underline-offset-2">Copy</button>
          </dd>
        </dl>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Funnel filter chips (client-side; all invites already fit in memory)
// ============================================================================

/** Rules that define each chip's inclusion criteria. Centralised so the chip
 *  UI and the filter application logic stay in sync. */
const FILTER_RULES = {
  all:            { label: "All",                     test: () => true },
  not_opened:     { label: "Not opened",              test: (i) => !i.opened_at },
  opened_no_sub:  { label: "Opened, not submitted",   test: (i) => !!i.opened_at && !i.captured_phone },
  match:          { label: "Match",                   test: (i) => i.phone_matched === true },
  mismatch:       { label: "Mismatch",                test: (i) => i.phone_matched === false },
  redirected:     { label: "Redirected",              test: (i) => !!i.redirected_at },
};

function matchFilter(invites, filter) {
  const rule = FILTER_RULES[filter] || FILTER_RULES.all;
  return invites.filter(rule.test);
}

function FunnelChips({ invites, filter, onChange }) {
  // Precompute counts once per render so chip labels update live as new
  // invites stream in via load()/bulk upload.
  const counts = useMemo(() => {
    const c = {};
    for (const key of Object.keys(FILTER_RULES)) {
      c[key] = invites.filter(FILTER_RULES[key].test).length;
    }
    return c;
  }, [invites]);

  const order = ["all", "not_opened", "opened_no_sub", "match", "mismatch", "redirected"];

  return (
    <div className="flex flex-wrap gap-2 my-3" data-testid="invite-filter-chips">
      {order.map((key) => {
        const active = filter === key;
        return (
          <button
            key={key}
            data-testid={`chip-${key}`}
            onClick={() => onChange(key)}
            className={
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors " +
              (active
                ? "bg-black text-white border-black"
                : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50")
            }
          >
            <span>{FILTER_RULES[key].label}</span>
            <span
              className={
                "inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-semibold px-1 " +
                (active ? "bg-white/20 text-white" : "bg-neutral-100 text-neutral-600")
              }
            >
              {counts[key]}
            </span>
          </button>
        );
      })}
    </div>
  );
}
